import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { PlessDialogService } from '../pless-dialog-service';
import { PlessService } from '../pless-service.model';
import { PlessResponse } from '../vo/pless-response.model';
import { PlessHttp } from './pless-http.service';
import { PlessRequest } from '../vo/pless-request.model';
import { tap } from 'rxjs/operators';

export interface ServiceInfo {
    url: string;
    method: string;
    contentType: string;
    showBusyCursor: boolean;
}

export interface PlessResponseHook<T> {
    hook(url: string, obj: T): T;
}

/**
 * APIと通信する際に使用する共通クラス
 * APIと通信するサービスは基本的にこのクラスを継承する
 *
 * @template T レスポンスのresult.objのobjの型
 */
@Injectable()
export abstract class PlessJsonResponder<T> {

    constructor(
        protected plessHttp: PlessHttp,
        protected dialogService: PlessDialogService,
        protected responseHook: PlessResponseHook<T> = null
    ) { }

    SYSTEM_ERROR_MESSAGE = 'システムエラーが発生しました。\nシステム管理者までご連絡ください。';
    errCode = [];

    // protected serviceInfo: ServiceInfo;

    /**
     * リクエスト送信
     *
     * @param id PlessServiceで指定したAPIのID
     * @param body 送信する内容
     */
    send(id: string, body: PlessRequest | HttpParams) {
        const serviceInfo = PlessService.SERVICE[id];
        this.result(this.getReponse$(id, body, serviceInfo), serviceInfo);
    }

    /**
     * リクエスト送信+Observable取得.
     * sendは、関数内でsubscribeするが、当該関数は取得したobservableを明示的にsubscribeする必要がある
     *
     * @param id PlessServiceで指定したAPIのID
     * @param body 送信する内容
     */
    send$(id: string, body: PlessRequest | HttpParams): Observable<HttpResponse<PlessResponse<T>>> {

        const serviceInfo = PlessService.SERVICE[id];
        const response$ = this.getReponse$(id, body, serviceInfo);

        if (response$ == null) {
            this.fault();
            return throwError('指定されたAPIのIDはPlessServiceに存在しません');
        }

        if (serviceInfo.showBusyCursor) {
            this.plessHttp.createBlockLayer();
        }

        return response$.pipe(
            tap({
                next: (response) => this.onSuccess(response, serviceInfo),
                error: (error) => this.onError(error, serviceInfo)
            }));
    }

    /**
     *
     * @param id PlessServiceで指定したAPIのID
     * @param body 送信する内容
     * @returns
     */
    private getReponse$(id: string, body: PlessRequest | HttpParams, serviceInfo: ServiceInfo): Observable<HttpResponse<PlessResponse<T>>> {

        if (serviceInfo == null) {
            console.log('指定されたAPIのIDはPlessServiceに存在しません');
            return null;
        }

        let response: Observable<HttpResponse<PlessResponse<T>>>;

        const csrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
        const jwt = document.cookie.split('; ').find(row => row.startsWith('PEACE_JWT='));

        if (serviceInfo.method === 'POST') {
            response = this.plessHttp.post<PlessResponse<T>>(serviceInfo.url, body, {
                headers: new HttpHeaders({
                    'Content-Type': serviceInfo.contentType,
                    'X-XSRF-TOKEN': csrfToken ? csrfToken.split('=')[1] : '',
                    Authorization: jwt ? 'Bearer ' + jwt.split('=')[1] : '',
                }), responseType: 'json',
                withCredentials: true
            });
        } else if (serviceInfo.method === 'GET') {
            let param = '';
            if (body != null) {
                param = '?' + body;
            } // TODO: 後で！
            response = this.plessHttp.get<PlessResponse<T>>(serviceInfo.url + param, {
                headers: new HttpHeaders({
                    'Content-Type': serviceInfo.contentType,
                    'X-XSRF-TOKEN': csrfToken ? csrfToken.split('=')[1] : '',
                    Authorization: jwt ? 'Bearer ' + jwt.split('=')[1] : '',
                }), responseType: 'json',
                withCredentials: true
            });
        }

        return response;
    }

    /**
     * 送信処理後の処理
     *
     * @param httpresponse
     */
    protected result(httpresponse: Observable<HttpResponse<PlessResponse<T>>>, serviceInfo: ServiceInfo) {

        if (serviceInfo.showBusyCursor) {
            this.plessHttp.createBlockLayer();
        }

        if (httpresponse == null) {
            this.fault();
            return;
        }

        httpresponse.subscribe({
            next: (response) => this.onSuccess(response, serviceInfo),
            error: (error) => this.onError(error, serviceInfo)
        });
    }

    /**
     * リクエスト成功時の処理
     * @param response
     */
    private onSuccess(response: HttpResponse<PlessResponse<T>>, serviceInfo: ServiceInfo): void {

        if (serviceInfo.showBusyCursor) {
            this.plessHttp.removeBlockLayer();
        }

        const rsp = response.body;
        if (rsp == null) {
            this.fault();

        } else if (rsp.status === 'ok') {
            const obj = this.responseHook ?
                this.responseHook.hook(serviceInfo.url, rsp.result?.obj) : rsp.result?.obj;
            this.success(obj);

        } else {
            this.statusError(rsp.status, rsp);
        }
    }

    /**
     * リクエスト失敗時の処理
     * @param error
     */
    private onError(error: any, serviceInfo: ServiceInfo): void {
        if (serviceInfo.showBusyCursor) {
            this.plessHttp.removeBlockLayer();
        }
        // リクエスト失敗時の処理
        this.fault();
    }

    /**
     * StatusがOK以外の場合の処理
     * 独自のステータス処理を実装したい場合はこのメソッドをオーバーライドする
     *
     * @param status statauタグの値
     * @param result レスポンスの内容(rspタグの値)
     */
    protected statusError(status: string, result: PlessResponse<T>) {
        switch (status.toLowerCase()) {
            case 'unauthorized':
                // "認証エラー"時の処理
                this.unauthorizedError(result);
                break;
            case 'forbidden':
                // "権限エラー"時の処理
                this.forbiddenError(result);
                break;
            case 'validationerror':
                // "検証エラー"時の処理
                this.validationError(result);
                break;
            case 'parametererror':
                // "パラメータエラー"時の処理
                this.parameterError(result);
                break;
            default:
                this.exceptionError(status, result);
                break;
        }
    }

    /**
     * 認証エラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected unauthorizedError(result: PlessResponse<T>) {
        this.showErrorMessage(result.message, 'E');
    }

    /**
     * 権限エラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected forbiddenError(result: PlessResponse<T>) {
        this.showErrorMessage(result.message, 'E');
    }

    /**
     * 検証エラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected validationError(result: PlessResponse<T>) {
        this.showErrorMessage(this.makeErrMsg(<Array<any>>result.result.obj), 'E');
    }

    /**
     * パラメータエラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected parameterError(result: PlessResponse<T>) {
        this.showErrorMessage(this.makeErrMsg(<Array<any>>result.result.obj), 'E');
    }

    /**
     * 特殊なエラーが返ってきた場合に呼び出されるメソッド。<br/>
     * 機能独自のステータスコードを持つ場合、このメソッドをオーバーライドして処理を実装する。
     */
    exceptionError(status: string, result: PlessResponse<T>) {
        let errMsg: string;
        if (status.toLowerCase() === 'systemerror') {
            errMsg = this.SYSTEM_ERROR_MESSAGE;
        } else {
            errMsg = result.message;
        }
        this.showErrorMessage(errMsg, 'E');
    }

    /**
     * リクエスト成功時のメソッド。
     * 成功時の処理を実装する場合、このメソッドを実装して処理を実装する。
     *
     * @param data dataタグの値
     */
    abstract success(data: T): void;

    /**
     * リクエスト失敗時のメソッド。
     * 失敗時の処理を実装する場合、このメソッドをオーバーライドして処理を実装する。
     */
    fault() {
        this.showErrorMessage('サーバへの接続に失敗しました。', 'S0104');
    }

    /**
     * バリデーションエラーメッセージをまとめた文字列を作成
     */
    private makeErrMsg(errList: Array<any>): string {
        let errMsgs = errList.map(err => `${err.Msg}`);
        return errMsgs.join('\n');
    }

    /**
     * 各エラーの発生時にエラーメッセージを表示するためのメソッド<br>
     * オーバーライドによる動作の変更が可能
     */
    protected showErrorMessage(message: string, title: string) {
        this.dialogService.openAlert(message, title);    
    }

    /**
     * 特殊なメッセージ（チェック結果など）をまとめた文字列を作成
     */
    makeParticularMsg(msgList: any): string {
        let message = '';
        if (msgList != null) {
            for (let i = 0; i < msgList.length; i++) {
                if (msgList[i] == null) {
                    message += '\n';
                } else {
                    message += msgList[i].toString() + '\n';
                }
            }
        }
        return message;
    }

}
